import { HubConnectionBuilder, HubConnectionState, LogLevel } from "@microsoft/signalr";
import { useEffect, useMemo, useRef } from "react";

import { useBackendUrl } from "~services/providers/BackendUrlProvider";
import { withRetries } from "~utils/url";

import { useFeature } from "./useFeature";
import { type  State,useSelector  } from "./useTypedRedux";


/** Must match API `MapHub<OperationalMapHub>("/hubs/operational-map")`. */
const hubRelativePath = "hubs/operational-map";

function buildHubUrl(baseUrl: string): string {
  const trimmed = baseUrl.trim().replace(/\/+$/, "");
  return `${trimmed}/${hubRelativePath.replace(/^\/+/, "")}`;
}

export type OperationalMapRealtimeArgs =
  | {
      mode: "staff";
      bubbleId: string;
      dayOfEmergencyId: string;
      enabled?: boolean;
      onParticipantStatusChanged?: () => void | Promise<void>;
    }
  | {
      mode: "lawEnforcement";
      dayOfEmergencyId: string;
      accessToken: string;
      enabled?: boolean;
      onParticipantStatusChanged?: () => void | Promise<void>;
    }
  | {
      mode: "recipient";
      dayOfEmergencyId: string;
      accessToken: string;
      enabled?: boolean;
      onParticipantStatusChanged?: () => void | Promise<void>;
    };

/**
 * Subscribes to operational-map SignalR updates (participant status / map pins) for a reunification day.
 * Staff uses JWT (`accessTokenFactory`); law enforcement and recipients use the same magic-link token as REST.
 */
export function useOperationalMapRealtime(args: OperationalMapRealtimeArgs): void {
  const { backendUrl } = useBackendUrl();
  const token = useSelector((s: State) => s.auth.token);
  const enableLogs = useFeature("chatLogs");
  const onEventRef = useRef(args.onParticipantStatusChanged);
  onEventRef.current = args.onParticipantStatusChanged;

  const hubUrl = useMemo(() => (backendUrl ? buildHubUrl(backendUrl) : null), [backendUrl]);

  const enabled = args.enabled ?? true;
  const mode = args.mode;
  const dayOfEmergencyId = args.dayOfEmergencyId;
  const bubbleId = mode === "staff" ? args.bubbleId : "";
  const accessToken = mode === "lawEnforcement" || mode === "recipient" ? args.accessToken : "";

  const canConnect = useMemo(() => {
    if (!hubUrl || !enabled || !dayOfEmergencyId) {
      return false;
    }
    if (mode === "staff") {
      return Boolean(bubbleId && token);
    }
    return Boolean(accessToken);
  }, [hubUrl, enabled, dayOfEmergencyId, mode, bubbleId, token, accessToken]);

  const connection = useMemo(() => {
    if (!hubUrl || !canConnect) {
      return null;
    }
    if (mode === "staff") {
      return new HubConnectionBuilder()
        .withUrl(hubUrl, {
          accessTokenFactory: () => token ?? "",
          logger: enableLogs ? LogLevel.Debug : LogLevel.None,
        })
        .withAutomaticReconnect()
        .build();
    }
    return new HubConnectionBuilder()
      .withUrl(hubUrl, {
        logger: enableLogs ? LogLevel.Debug : LogLevel.None,
      })
      .withAutomaticReconnect()
      .build();
  }, [hubUrl, canConnect, mode, token, enableLogs]);

  useEffect(() => {
    if (!connection || !backendUrl || !dayOfEmergencyId) {
      return;
    }

    const join = async () => {
      if (mode === "staff") {
        const ok = await withRetries(async () => {
          await connection.invoke("JoinStaff", dayOfEmergencyId, bubbleId);
        });
        if (!ok) {
          throw new Error("Could not join operational map (staff).");
        }
        return;
      }
      if (mode === "lawEnforcement") {
        const ok = await withRetries(async () => {
          await connection.invoke("JoinLawEnforcement", dayOfEmergencyId, accessToken);
        });
        if (!ok) {
          throw new Error("Could not join operational map (law enforcement).");
        }
        return;
      }
      const ok = await withRetries(async () => {
        await connection.invoke("JoinRecipient", dayOfEmergencyId, accessToken);
      });
      if (!ok) {
        throw new Error("Could not join operational map (recipient).");
      }
    };

    const onParticipantStatusChanged = (payload: unknown) => {
      let id = "";
      if (typeof payload === "string") {
        id = payload;
      } else if (payload != null && typeof payload === "object") {
        const o = payload as Record<string, unknown>;
        id =
          typeof o.dayOfEmergencyId === "string"
            ? o.dayOfEmergencyId
            : typeof o.value === "string"
              ? o.value
              : String(payload);
      }
      const normalized = id.replace(/[{}]/g, "").trim();
      if (!normalized || normalized.toLowerCase() !== dayOfEmergencyId.toLowerCase()) {
        return;
      }
      void Promise.resolve(onEventRef.current?.());
    };

    connection.on("ParticipantStatusChanged", onParticipantStatusChanged);
    connection.on("participantStatusChanged", onParticipantStatusChanged);

    connection.onreconnected(async () => {
      try {
        await join();
      } catch (e) {
        console.error("Operational map re-join failed", e);
      }
    });

    let cancelled = false;

    void (async () => {
      try {
        const started = await withRetries(async () => {
          await connection.start();
        });
        if (!started || cancelled) {
          return;
        }
        if (connection.state !== HubConnectionState.Connected) {
          return;
        }
        await join();
      } catch (e) {
        if (!cancelled) {
          console.error("Operational map connection failed", e);
        }
      }
    })();

    return () => {
      cancelled = true;
      connection.off("ParticipantStatusChanged", onParticipantStatusChanged);
      connection.off("participantStatusChanged", onParticipantStatusChanged);
      void (async () => {
        try {
          if (connection.state === HubConnectionState.Connected) {
            await connection.stop();
          }
        } catch {
          /* ignore */
        }
      })();
    };
  }, [accessToken, backendUrl, bubbleId, connection, dayOfEmergencyId, mode]);
}
